import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Rocket, ArrowRight, Clock, Calendar } from 'lucide-react';
import '@/styles/boost.css';
import '@/styles/journal.css';
import Starfield from '@/components/Starfield';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { BackToTop } from '@/components/BackToTop';
import { useSeo } from '@/lib/seo';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://boostmarketing.agency';

export const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(String(iso).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

export function PostCard({ post, testId }) {
  return (
    <Link to={`/journal/${post.slug}`} className="jr-card" data-testid={testId || `journal-card-${post.slug}`}>
      {post.cover_image && (
        <div className="jr-card-media">
          <img src={post.cover_image} alt={post.cover_alt || post.title} loading="lazy" />
        </div>
      )}
      <div className="jr-card-body">
        <div className="jr-card-meta">
          <span>{fmtDate(post.published_at)}</span>
          <span>{post.reading_minutes} min read</span>
        </div>
        <h3 className="jr-card-title">{post.title}</h3>
        <p className="jr-card-excerpt">{post.excerpt}</p>
        <span className="jr-card-more">Read article <ArrowRight size={15} /></span>
      </div>
    </Link>
  );
}

export default function JournalIndex() {
  const { tagSlug } = useParams();
  const [posts, setPosts] = useState(null);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    let live = true;
    Promise.all([
      fetch(`${API}/api/blog/posts`).then((r) => r.json()),
      fetch(`${API}/api/blog/tags`).then((r) => r.json()),
    ])
      .then(([p, t]) => {
        if (!live) return;
        setPosts(Array.isArray(p) ? p : []);
        setTags(Array.isArray(t) ? t : []);
      })
      .catch(() => live && setPosts([]));
    return () => { live = false; };
  }, []);

  const activeTag = tagSlug ? tags.find((t) => t.slug === tagSlug) : null;
  const visible = (posts || []).filter(
    (p) => !tagSlug || (p.tags || []).some((t) => t.toLowerCase().replace(/[^a-z0-9]+/g, '-') === tagSlug)
  );
  const [featured, ...rest] = visible;

  const title = activeTag
    ? `${activeTag.tag} articles | The Boost! Journal`
    : 'The Journal — marketing, web design & AI visibility | BOOST!';
  const description = activeTag
    ? `${activeTag.tag} insight, case studies and how-tos from BOOST! Design & Marketing — a design, web and AI marketing agency in Saffron Walden, Essex.`
    : 'Case studies, local SEO and GEO insight from BOOST! Design & Marketing — the design, web and AI marketing agency in Saffron Walden, serving Cambridge, Bishop\'s Stortford, Haverhill and Great Dunmow.';
  const canonical = activeTag ? `${SITE}/journal/tag/${activeTag.slug}` : `${SITE}/journal`;

  useSeo({
    title,
    description,
    canonical,
    image: `${SITE}/og-home.png`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        '@id': `${SITE}/journal#blog`,
        name: 'The Boost! Journal',
        url: `${SITE}/journal`,
        description,
        inLanguage: 'en-GB',
        publisher: {
          '@type': 'Organization',
          name: 'BOOST! Design & Marketing',
          url: SITE,
          address: {
            '@type': 'PostalAddress',
            streetAddress: '27 Fairycroft Road',
            addressLocality: 'Saffron Walden',
            addressRegion: 'Essex',
            postalCode: 'CB10 1LZ',
            addressCountry: 'GB',
          },
        },
        about: ['Marketing', 'Web design', 'Local SEO', 'Generative Engine Optimisation'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Journal', item: `${SITE}/journal` },
        ],
      },
    ],
  });

  return (
    <div className="boost-app" data-testid="journal-index">
      <Starfield />
      <Navbar />
      <main>
        <section className="section jr-hero">
          <div className="section-inner">
            <span className="section-eyebrow">MISSION LOGS</span>
            <h1 className="jr-h1">
              {activeTag ? <>{activeTag.tag} <span className="text-pink">articles</span></> : <>The <span className="text-pink">Journal</span></>}
            </h1>
            <p className="jr-hero-sub">
              Case studies, local SEO and GEO playbooks from our studio on Fairycroft Road in Saffron Walden —
              written for businesses across Essex, Hertfordshire, Suffolk and Cambridgeshire.
            </p>
            <div className="jr-tags" data-testid="journal-tag-list">
              <Link to="/journal" className={`jr-tag ${!tagSlug ? 'jr-tag-on' : ''}`} data-testid="journal-tag-all">All</Link>
              {tags.map((t) => (
                <Link
                  key={t.slug}
                  to={`/journal/tag/${t.slug}`}
                  className={`jr-tag ${tagSlug === t.slug ? 'jr-tag-on' : ''}`}
                  data-testid={`journal-tag-${t.slug}`}
                >
                  {t.tag} <span className="jr-tag-count">{t.count}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 30 }}>
          <div className="section-inner">
            {posts === null && <p className="jr-empty" data-testid="journal-loading">Loading the logs…</p>}
            {posts && visible.length === 0 && (
              <p className="jr-empty" data-testid="journal-empty">No articles here yet — new mission logs are on the way. 🚀</p>
            )}
            {featured && (
              <Link to={`/journal/${featured.slug}`} className="jr-featured" data-testid="journal-featured">
                {featured.cover_image && (
                  <div className="jr-featured-media">
                    <img src={featured.cover_image} alt={featured.cover_alt || featured.title} />
                  </div>
                )}
                <div className="jr-featured-body">
                  <span className="jr-badge">Latest</span>
                  <h2 className="jr-featured-title">{featured.title}</h2>
                  <p className="jr-featured-excerpt">{featured.excerpt}</p>
                  <div className="jr-card-meta">
                    <span><Calendar size={13} style={{ verticalAlign: '-2px' }} /> {fmtDate(featured.published_at)}</span>
                    <span><Clock size={13} style={{ verticalAlign: '-2px' }} /> {featured.reading_minutes} min read</span>
                  </div>
                  <span className="jr-card-more">Read article <ArrowRight size={15} /></span>
                </div>
              </Link>
            )}
            {rest.length > 0 && (
              <div className="jr-grid" data-testid="journal-grid">
                {rest.map((p) => <PostCard key={p.slug} post={p} />)}
              </div>
            )}
            <div style={{ marginTop: 48, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/quote" className="boost-btn boost-btn-primary" data-testid="journal-quote-cta">
                <Rocket size={16} /> Build a quote in 2 minutes
              </Link>
              <a href={`${API}/api/blog/rss.xml`} className="boost-btn boost-btn-ghost" data-testid="journal-rss-link" target="_blank" rel="noopener noreferrer">
                RSS feed
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
